import { useEffect, useState } from "react";
import { Terminal, Apple, Code2, Check, Copy, AppWindow } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/toast";
import { cn } from "@/lib/utils";
import { ws } from "@/lib/ws";

type Platform = "linux" | "mac" | "api";
type Step = "choose" | "install" | "key" | "done";

/** One-liners lifted verbatim from internal/cli/install.sh and
 * install-macos.sh — keep these in sync if those scripts' documented
 * entrypoints ever change. */
const LINUX_INSTALL = "curl -fsSL https://apt.pure.dev/install.sh | sudo sh";
const MAC_INSTALL = "curl -fsSL https://brew.pure.dev/install.sh | sh";

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative">
      <pre className="rounded-md bg-muted px-4 py-3 pr-11 text-sm font-mono overflow-x-auto">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        aria-label="Copy to clipboard"
        onClick={async () => {
          await navigator.clipboard.writeText(code);
          setCopied(true);
          toast("Copied");
          setTimeout(() => setCopied(false), 1500);
        }}
        className="absolute right-2 top-2 rounded-md p-1.5 text-muted-foreground hover:bg-background hover:text-foreground transition-colors"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function PlatformCard({
  icon,
  title,
  description,
  onClick,
  disabled,
  badge,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  /** Renders as visibly non-interactive (dimmed, no hover, not-allowed
   * cursor) and skips onClick entirely — for platforms that are listed so
   * people know they're planned, but aren't installable yet. */
  disabled?: boolean;
  /** Small pill shown next to the title, e.g. "Coming soon". */
  badge?: string;
}) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled}
      className={cn(
        "flex flex-col items-start gap-2 rounded-lg border p-5 text-left transition-colors",
        disabled
          ? "opacity-50 cursor-not-allowed"
          : "hover:border-primary hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      )}
    >
      <div
        className={cn(
          "rounded-md p-2",
          disabled
            ? "bg-muted text-muted-foreground"
            : "bg-primary/10 text-primary",
        )}
      >
        {icon}
      </div>
      <div className="flex items-center gap-2">
        <span className="font-medium">{title}</span>
        {badge && (
          <span className="rounded-full border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {badge}
          </span>
        )}
      </div>
      <div className="text-sm text-muted-foreground">{description}</div>
    </button>
  );
}

export function OnboardingModal({
  open,
  onDone,
}: {
  open: boolean;
  onDone: () => void;
}) {
  const [step, setStep] = useState<Step>("choose");
  const [platform, setPlatform] = useState<Platform | null>(null);
  const [keyName, setKeyName] = useState("my-laptop");
  const [creating, setCreating] = useState(false);
  const [rawKey, setRawKey] = useState<string | null>(null);
  const [keyError, setKeyError] = useState<string | null>(null);

  // Reset to the start each time the modal is (re-)opened, so a user who
  // skips and later reopens it (there's no reopen entry point yet, but
  // this keeps the component correct if one gets added later) doesn't
  // land mid-flow.
  useEffect(() => {
    if (open) {
      setStep("choose");
      setPlatform(null);
      setKeyName("my-laptop");
      setRawKey(null);
      setKeyError(null);
    }
  }, [open]);

  useEffect(() => {
    const offCreated = ws.on("keys.created", (m) => {
      setCreating(false);
      setRawKey(m.raw);
    });
    const offErr = ws.on("error", (m) => {
      if (m.req_type === "keys.create") {
        setCreating(false);
        setKeyError(m.message);
      }
    });
    return () => {
      offCreated();
      offErr();
    };
  }, []);

  function finish() {
    ws.send({ type: "onboarding.complete" });
    onDone();
  }

  function choosePlatform(p: Platform) {
    setPlatform(p);
    setStep(p === "api" ? "key" : "install");
  }

  function createKey() {
    if (!keyName.trim() || creating) return;
    setKeyError(null);
    setCreating(true);
    ws.send({
      type: "keys.create",
      name: keyName.trim(),
      scopes: ["compile:submit"],
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && finish()}>
      <DialogContent className="max-w-2xl" hideCloseButton>
        {step === "choose" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">Welcome to Pure 👋</DialogTitle>
              <DialogDescription>
                Let&apos;s get your first build running — it only takes a
                minute. How are you planning to use Pure?
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-2">
              <PlatformCard
                icon={<Terminal className="h-5 w-5" />}
                title="Linux"
                description="Install the CLI with one command."
                onClick={() => choosePlatform("linux")}
              />
              <PlatformCard
                icon={<Apple className="h-5 w-5" />}
                title="Mac"
                description="Install the CLI with one command."
                onClick={() => choosePlatform("mac")}
              />
              <PlatformCard
                icon={<Code2 className="h-5 w-5" />}
                title="API"
                description="Call the build API directly, no CLI."
                onClick={() => choosePlatform("api")}
              />
              <PlatformCard
                icon={<AppWindow className="h-5 w-5" />}
                title="Windows"
                badge="Coming soon"
                description="Not available yet — check back soon."
                onClick={() => {}}
                disabled
              />
            </div>
            <div className="mt-2 text-right">
              <Button variant="ghost" size="sm" onClick={finish}>
                Skip for now
              </Button>
            </div>
          </>
        )}

        {step === "install" && platform !== "api" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">
                Install the CLI for {platform === "linux" ? "Linux" : "Mac"}
              </DialogTitle>
              <DialogDescription>
                Paste this into your terminal. It installs the{" "}
                <span className="font-mono">pure</span> command — no other setup
                required.
              </DialogDescription>
            </DialogHeader>
            <div className="my-4 space-y-3">
              <CodeBlock
                code={platform === "linux" ? LINUX_INSTALL : MAC_INSTALL}
              />
              {platform === "mac" && (
                <p className="text-sm text-muted-foreground">
                  The build isn&apos;t notarized yet, so the first time you run{" "}
                  <span className="font-mono">pure</span>, macOS will warn that
                  it&apos;s from an unidentified developer — the installer
                  already clears the quarantine flag for you, so you can ignore
                  that and continue normally.
                </p>
              )}
            </div>
            <div className="flex items-center justify-between mt-2">
              <Button variant="ghost" size="sm" onClick={finish}>
                Skip for now
              </Button>
              <Button onClick={() => setStep("key")}>
                Next: get an API key
              </Button>
            </div>
          </>
        )}

        {step === "key" && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl">
                {rawKey ? "You're all set 🎉" : "Create your first API key"}
              </DialogTitle>
              <DialogDescription>
                {rawKey
                  ? "Save this key somewhere safe — it will never be shown again."
                  : "You'll need a key to authenticate. This one only needs to submit builds."}
              </DialogDescription>
            </DialogHeader>

            {!rawKey ? (
              <>
                <div className="space-y-1.5 my-4">
                  <Label htmlFor="onboarding-key-name">Key name</Label>
                  <Input
                    id="onboarding-key-name"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    placeholder="my-laptop"
                  />
                  {keyError && (
                    <p className="text-xs text-destructive">{keyError}</p>
                  )}
                </div>
                <div className="flex items-center justify-between mt-2">
                  <Button variant="ghost" size="sm" onClick={finish}>
                    Skip for now
                  </Button>
                  <Button
                    onClick={createKey}
                    disabled={!keyName.trim() || creating}
                  >
                    {creating ? "Creating…" : "Generate key"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className="my-4 space-y-4">
                  <CodeBlock code={rawKey} />

                  {platform === "api" ? (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        Use it directly against the API:
                      </p>
                      <CodeBlock
                        code={[
                          "curl -X POST https://api.pure.dev/v1/build \\",
                          '  -H "Authorization: Bearer <your key>" \\',
                          '  -F "entry=server.ts" \\',
                          '  -F "source=@." ',
                        ].join("\n")}
                      />
                      <p className="text-xs text-muted-foreground">
                        Full request/response reference is in the API docs.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm font-medium">
                        Store it for the CLI:
                      </p>
                      <CodeBlock code="pure login" />
                      <p className="text-xs text-muted-foreground">
                        Paste the key above when prompted — it&apos;s saved to{" "}
                        <span className="font-mono">
                          ~/.config/pure/credentials
                        </span>
                        . Then run{" "}
                        <span className="font-mono">pure build server.ts</span>{" "}
                        to compile your first project.
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-2 text-right">
                  <Button onClick={finish}>Done</Button>
                </div>
              </>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
